/**
 * The aiming gesture.
 *  - Bearing: the column under the pointer. Press on the coconut and the arm points down its
 *    column; dragging sideways follows. On the keyboard, A/D or ←/→ nudge it.
 *  - Elevation: the row you press on (rail = flat, horizon = 45°, sky = lob; see `elevationAt`),
 *    then drag up/down to fine-tune. On the keyboard W/S or ↑/↓, which keep their value.
 *  - Power: time. While the pointer (or Space) is held a meter rises and falls like a golf swing
 *    meter; release to fire at whatever it reads. A tap fires with almost nothing.
 * Acts on pointer events, never click.
 */
import { debugLog } from '../ui/dom.ts';
import { PHYSICS } from './engine.ts';
import { azimuthAt, elevationAt, type View } from './project.ts';

export interface Aim {
  /** degrees right of straight ahead */
  azimuth: number;
  /** degrees above the horizontal */
  elevation: number;
  /** 0..1; meaningful only while charging */
  power: number;
  /** is a shot being charged right now (meter running)? */
  charging: boolean;
}

/** seconds for the meter to rise to full and fall back to nothing */
export const CHARGE_PERIOD = 1.8;
/** degrees per key press / key repeat */
const KEY_STEP = 2;
/** how long a keyboard-adjusted aim stays on screen without charging (ms) */
const SHOW_AFTER_KEY_MS = 1500;

const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));

/** elevation from a drag of dy css px upward, starting from `base`, on a canvas h px tall */
export function elevationFromDrag(base: number, dy: number, h: number): number {
  return clamp(base + (dy / (0.3 * h)) * (PHYSICS.elevationMax - PHYSICS.elevationMin), PHYSICS.elevationMin, PHYSICS.elevationMax);
}

/** the meter: a triangle wave 0 → 1 → 0 every CHARGE_PERIOD seconds of holding */
export function powerAt(heldSeconds: number): number {
  const u = (Math.max(0, heldSeconds) / (CHARGE_PERIOD / 2)) % 2;
  return u <= 1 ? u : 2 - u;
}

export interface InputHandlers {
  /** may the player start a shot right now? */
  canAim(): boolean;
  onFire(aim: Aim): void;
  view(): View;
  /** keys 1–4 pick from the bucket; tab/q cycles */
  onItem(which: number | 'next'): void;
}

export interface Input {
  /** the aim to show, with the power the meter reads at `nowMs`, or null when idle */
  current(nowMs: number): Aim | null;
  detach(): void;
}

export function attachInput(canvas: HTMLCanvasElement, handlers: InputHandlers): Input {
  let azimuth = 0;
  let elevation = 20;
  let press: { id: number; y0: number; el0: number; t: number } | null = null;
  let spaceSince: number | null = null;
  let lastKeyAt = -Infinity;

  const local = (e: PointerEvent): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const charging = (): boolean => press !== null || spaceSince !== null;
  const heldSince = (): number => (press ? press.t : spaceSince!);
  const aimAt = (nowMs: number): Aim => ({
    azimuth,
    elevation,
    power: charging() ? powerAt((nowMs - heldSince()) / 1000) : 0,
    charging: charging(),
  });

  const log = (e: PointerEvent): void =>
    debugLog(`${e.type} id=${e.pointerId} p=${e.isPrimary ? 1 : 0} ${e.pointerType} ${Math.round(e.clientX)},${Math.round(e.clientY)} ts=${Math.round(e.timeStamp)} now=${Math.round(performance.now())} press=${press ? 1 : 0} can=${handlers.canAim() ? 1 : 0}`);

  const down = (e: PointerEvent): void => {
    log(e);
    if (!e.isPrimary || charging() || !handlers.canAim()) return;
    const v = handlers.view();
    const { x, y } = local(e);
    azimuth = azimuthAt(v, x);
    elevation = elevationAt(v, y);
    press = { id: e.pointerId, y0: e.clientY, el0: elevation, t: e.timeStamp };
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events have no pointer to capture */
    }
  };
  const move = (e: PointerEvent): void => {
    if (!press || e.pointerId !== press.id) return;
    const v = handlers.view();
    azimuth = azimuthAt(v, local(e).x);
    elevation = elevationFromDrag(press.el0, press.y0 - e.clientY, v.h);
  };
  const up = (e: PointerEvent): void => {
    log(e);
    if (!press || e.pointerId !== press.id) return;
    move(e);
    const aim = aimAt(e.timeStamp);
    press = null;
    handlers.onFire(aim);
  };
  const cancel = (e: PointerEvent): void => {
    log(e);
    if (press && e.pointerId === press.id) press = null;
  };
  /** typing in a name box must not steer the arm */
  const typing = (e: KeyboardEvent): boolean => {
    const t = e.target as HTMLElement | null;
    return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
  };
  const keydown = (e: KeyboardEvent): void => {
    if (typing(e)) return;
    const k = e.key.toLowerCase();
    if (k === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (!e.repeat && !charging() && handlers.canAim()) spaceSince = e.timeStamp;
      return;
    }
    if (k === '1' || k === '2' || k === '3' || k === '4' || k === '5' || k === 'q' || k === 'tab') {
      e.preventDefault();
      handlers.onItem(k === 'q' || k === 'tab' ? 'next' : Number(k) - 1);
      return;
    }
    const dAz = k === 'arrowleft' || k === 'a' ? -KEY_STEP : k === 'arrowright' || k === 'd' ? KEY_STEP : 0;
    const dEl = k === 'arrowup' || k === 'w' ? KEY_STEP : k === 'arrowdown' || k === 's' ? -KEY_STEP : 0;
    if (!dAz && !dEl) return;
    e.preventDefault();
    azimuth = clamp(azimuth + dAz, -PHYSICS.azimuthMax, PHYSICS.azimuthMax);
    elevation = clamp(elevation + dEl, PHYSICS.elevationMin, PHYSICS.elevationMax);
    lastKeyAt = e.timeStamp;
  };
  const keyup = (e: KeyboardEvent): void => {
    if (typing(e)) return;
    if ((e.key === ' ' || e.code === 'Space') && spaceSince !== null) {
      const aim = aimAt(e.timeStamp);
      spaceSince = null;
      handlers.onFire(aim);
    }
  };
  const blur = (): void => {
    press = null;
    spaceSince = null;
  };

  canvas.addEventListener('pointerdown', down);
  canvas.addEventListener('pointermove', move);
  canvas.addEventListener('pointerup', up);
  canvas.addEventListener('pointercancel', cancel);
  window.addEventListener('keydown', keydown);
  window.addEventListener('keyup', keyup);
  window.addEventListener('blur', blur);
  return {
    current: (nowMs) => (charging() || nowMs - lastKeyAt < SHOW_AFTER_KEY_MS ? aimAt(nowMs) : null),
    detach: () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', keydown);
      window.removeEventListener('keyup', keyup);
      window.removeEventListener('blur', blur);
    },
  };
}
